/** Client-side VAT accounting helpers (mirrors backend/utils/vatAccounting.ts for API fallback). */

export const DEFAULT_VAT_RATE = 0.2;

export interface AccountingPeriod {
  period: string;
  dateFrom: Date;
  dateTo: Date;
}

export interface VatQuarterRow {
  quarter: number;
  label: string;
  output_vat: number;
  input_vat: number;
  net_vat: number;
  invoice_count: number;
  expense_count: number;
}

export interface VatReportResult {
  period: string;
  date_from: string;
  date_to: string;
  output_vat: number;
  input_vat: number;
  net_vat: number;
  quarters: VatQuarterRow[];
  total_year?: number;
}

const QUARTER_LABELS: Record<number, string> = {
  1: 'Q1 (Jan–Mar)', 2: 'Q2 (Apr–Jun)', 3: 'Q3 (Jul–Sep)', 4: 'Q4 (Oct–Dec)',
};

export function roundMoney(value: number): number {
  return Math.round((Number(value) || 0) * 100) / 100;
}

export function toDateOnly(value: Date): string {
  return value.toISOString().split('T')[0];
}

/** Normalize DB date/timestamp values to YYYY-MM-DD for comparisons. */
export function normalizeDateStr(value: unknown): string {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return value.toISOString().split('T')[0];
  }
  const s = String(value).trim();
  const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }
  return '';
}

export function parseAccountingPeriod(period = '3m'): AccountingPeriod {
  const now = new Date();
  const dateTo = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (period === 'all') {
    return { period: 'all', dateFrom: new Date(2000, 0, 1), dateTo };
  }
  let months = 3;
  if (period === '1y') months = 12;
  else {
    const parsed = parseInt(String(period).replace(/[^0-9]/g, ''), 10);
    if (!Number.isNaN(parsed) && parsed > 0) months = parsed;
  }
  const dateFrom = new Date(dateTo.getFullYear(), dateTo.getMonth() - months, dateTo.getDate());
  return { period, dateFrom, dateTo };
}

export function splitGrossInclusive(gross: number, rate = DEFAULT_VAT_RATE) {
  const grossAmount = roundMoney(gross);
  if (grossAmount <= 0) return { net: 0, vat: 0, gross: 0 };
  const net = roundMoney(grossAmount / (1 + rate));
  return { net, vat: roundMoney(grossAmount - net), gross: grossAmount };
}

export function splitNetExclusive(net: number, rate = DEFAULT_VAT_RATE) {
  const netAmount = roundMoney(net);
  if (netAmount <= 0) return { net: 0, vat: 0, gross: 0 };
  const vat = roundMoney(netAmount * rate);
  return { net: netAmount, vat, gross: roundMoney(netAmount + vat) };
}

export function paymentNetVat(paymentAmount: number, invoice?: any) {
  const gross = roundMoney(paymentAmount);
  if (gross <= 0) return { net: 0, vat: 0, gross: 0 };
  const invoiceGross = roundMoney(Number(invoice?.amount ?? invoice?.total ?? 0));
  const invoiceNet = roundMoney(Number(invoice?.subtotal ?? 0));
  const invoiceVat = roundMoney(Number(invoice?.total_vat ?? invoice?.totalVat ?? 0));
  if (invoiceGross > 0 && (invoiceNet > 0 || invoiceVat > 0)) {
    return {
      net: roundMoney(gross * (invoiceNet / invoiceGross)),
      vat: roundMoney(gross * (invoiceVat / invoiceGross)),
      gross,
    };
  }
  return splitGrossInclusive(gross);
}

export function enrichLedgerEntry(entry: any) {
  const gross = roundMoney(Number(entry.amount ?? 0));
  if (entry.amount_net != null && entry.vat_amount != null) {
    return { ...entry, amount_gross: gross, amount_net: roundMoney(entry.amount_net), vat_amount: roundMoney(entry.vat_amount) };
  }
  if (entry.source === 'payment') {
    const split = paymentNetVat(gross, {
      subtotal: entry.invoice_subtotal,
      total_vat: entry.invoice_total_vat,
      amount: entry.invoice_amount,
      total: entry.invoice_total,
    });
    return { ...entry, amount_gross: gross, amount_net: split.net, vat_amount: split.vat };
  }
  if (entry.type === 'income' && entry.category === 'Credit Applied') {
    return { ...entry, amount_gross: gross, amount_net: gross, vat_amount: 0 };
  }
  if (entry.type === 'expense' && entry.reference_type === 'po_receive') {
    const split = splitNetExclusive(gross);
    return { ...entry, amount_gross: split.gross, amount_net: split.net, vat_amount: split.vat };
  }
  if (entry.type === 'expense') {
    const split = splitGrossInclusive(gross);
    return { ...entry, amount_gross: gross, amount_net: split.net, vat_amount: split.vat };
  }
  if (entry.type === 'income') {
    const split = splitGrossInclusive(gross);
    return { ...entry, amount_gross: gross, amount_net: split.net, vat_amount: split.vat };
  }
  return { ...entry, amount_gross: gross, amount_net: gross, vat_amount: 0 };
}

function quarterForDate(dateStr: string): number {
  const d = new Date(normalizeDateStr(dateStr) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return 0;
  return Math.floor(d.getMonth() / 3) + 1;
}

export function buildVatReportFromLedgerAndInvoices(period: AccountingPeriod, ledgerEntries: any[], invoices: any[]): VatReportResult {
  const dateFromStr = toDateOnly(period.dateFrom);
  const dateToStr = toDateOnly(period.dateTo);
  const quarterMap = new Map<number, VatQuarterRow>();

  const ensureQuarter = (q: number): VatQuarterRow => {
    if (!quarterMap.has(q)) {
      quarterMap.set(q, {
        quarter: q,
        label: QUARTER_LABELS[q] || `Q${q}`,
        output_vat: 0,
        input_vat: 0,
        net_vat: 0,
        invoice_count: 0,
        expense_count: 0,
      });
    }
    return quarterMap.get(q)!;
  };

  for (const inv of invoices) {
    if (['Cancelled', 'Draft', 'Void'].includes(inv.status)) continue;
    const dateStr = normalizeDateStr(inv.invoice_date || inv.invoiceDate || inv.created_at);
    if (!dateStr || dateStr < dateFromStr || dateStr > dateToStr) continue;
    const row = ensureQuarter(quarterForDate(dateStr));
    row.output_vat = roundMoney(row.output_vat + Number(inv.total_vat ?? inv.totalVat ?? inv.vat_amount ?? 0));
    row.invoice_count += 1;
  }

  for (const entry of ledgerEntries) {
    if (entry.type !== 'expense') continue;
    const dateStr = normalizeDateStr(entry.date || entry.created_at);
    if (!dateStr || dateStr < dateFromStr || dateStr > dateToStr) continue;
    const row = ensureQuarter(quarterForDate(dateStr));
    row.input_vat = roundMoney(row.input_vat + Number(entry.vat_amount ?? 0));
    row.expense_count += 1;
  }

  const quarters = Array.from(quarterMap.values())
    .sort((a, b) => a.quarter - b.quarter)
    .map(row => ({ ...row, net_vat: roundMoney(row.output_vat - row.input_vat) }));

  const output_vat = roundMoney(quarters.reduce((s, q) => s + q.output_vat, 0));
  const input_vat = roundMoney(quarters.reduce((s, q) => s + q.input_vat, 0));

  return {
    period: period.period,
    date_from: dateFromStr,
    date_to: dateToStr,
    output_vat,
    input_vat,
    net_vat: roundMoney(output_vat - input_vat),
    quarters,
    total_year: roundMoney(output_vat - input_vat),
  };
}

export function computeLedgerTotals(entries: any[]) {
  const totals = entries.reduce(
    (acc, entry) => {
      const net = roundMoney(Number(entry.amount_net ?? entry.amount ?? 0));
      if (entry.type === 'income') {
        acc.income_net += net;
      } else {
        acc.expense_net += net;
      }
      return acc;
    },
    { income_net: 0, expense_net: 0 }
  );
  return {
    income_net: roundMoney(totals.income_net),
    expense_net: roundMoney(totals.expense_net),
    netProfit: roundMoney(totals.income_net - totals.expense_net),
  };
}
